/**
 * debtPlanApi.js — Phase 86.E.
 *
 * Client for the canonical debt plan (`GET /api/debt-plan`). Read-only.
 * Returns `null` on failure so callers render "couldn't load" rather than a
 * confident empty state.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

async function authHeaders() {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    await new Promise((r) => setTimeout(r, 700));
    ({ data: { session } } = await supabase.auth.getSession());
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchDebtPlan({ extraMonthly = 0, extraCurrency = '', group = '', fresh = false } = {},
  { retry = true } = {}) {
  const params = new URLSearchParams();
  if (extraMonthly > 0) params.set('extra_monthly', String(extraMonthly));
  if (extraCurrency) params.set('extra_currency', extraCurrency);
  if (group) params.set('group', group);
  // Balances are written straight to Supabase, so a refetch after a local change
  // has to tell the server not to answer from its memo.
  if (fresh) params.set('fresh', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  try {
    const res = await axios.get(`${API}/api/debt-plan${qs}`, {
      headers: await authHeaders(), timeout: 45000,
    });
    return res.data;
  } catch (e) {
    if (retry) {
      await new Promise((r) => setTimeout(r, 900));
      return fetchDebtPlan({ extraMonthly, extraCurrency, group, fresh }, { retry: false });
    }
    console.warn('debt-plan fetch failed:', e?.response?.status || e?.message);
    return null;
  }
}

export default fetchDebtPlan;
